import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { innovationCells } from "@/mocks/homeData";

const cellIcons: Record<string, string> = {
  "Textile Engineering Cell": "ri-settings-3-line",
  "Management Excellence Cell": "ri-team-line",
  "Textile Processing Cell": "ri-drop-line",
  "Textile Materials Cell": "ri-stack-line",
  "Marketing & Merchandising Cell": "ri-shopping-cart-2-line",
  "Textile Efficiency Cell": "ri-speed-line",
};

const cellRoutes: Record<string, string> = {
  "Textile Engineering Cell": "/cells/textile-engineering",
  "Management Excellence Cell": "/cells/management-excellence",
  "Textile Processing Cell": "/cells/textile-processing",
  "Textile Materials Cell": "/cells/textile-materials",
  "Marketing & Merchandising Cell": "/cells/marketing-merchandising",
  "Textile Efficiency Cell": "/cells/textile-efficiency",
};

export default function InnovationCellsSection() {
  return (
    <section className="bg-tie-grey-light py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
              <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                / Specialized Innovation Cells
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl font-bold text-tie-text-primary leading-tight">
              Domain-Driven Innovation Cells
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
              Six specialized cells drive innovation across every critical
              domain of the textile ecosystem - from engineering and processing
              to marketing and efficiency.
            </p>
          </div>
        </ScrollReveal>

        {/* Cells Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
          {innovationCells.map((cell, index) => {
            const route = cellRoutes[cell.name];
            const CellCard = (
              <div className="group h-full bg-white rounded-2xl p-5 md:p-6 border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg transition-all duration-300">
                {/* Top: Icon */}
                <div className="flex items-start justify-between mb-4 md:mb-5">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-tie-teal/10 flex items-center justify-center">
                    <i
                      className={`${cellIcons[cell.name] || "ri-lightbulb-line"} text-tie-teal text-xl md:text-2xl`}
                    />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-tie-grey-light flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-tie-grey-dark">
                    <i className="ri-arrow-right-line text-tie-teal text-sm" />
                  </div>
                </div>

                {/* Cell Name */}
                <h3 className="text-base md:text-lg font-bold text-tie-text-primary mb-2">
                  {cell.name}
                </h3>

                {/* Focus Areas */}
                <div className="space-y-1.5 mt-3 md:mt-4">
                  {cell.focus.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-2 text-sm text-tie-text-secondary"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-tie-teal shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-tie-grey-dark">
                  <span className="inline-flex items-center text-sm font-medium text-tie-teal group-hover:text-tie-teal-dark transition-colors">
                    Explore cell
                    <i className="ri-arrow-right-line ml-1.5 text-xs group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            );

            return (
              <ScrollReveal key={cell.name} delay={index * 100}>
                <Link to={route} className="block cursor-pointer">
                  {CellCard}
                </Link>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Bottom Note */}
        <ScrollReveal delay={300}>
          <div className="max-w-3xl mx-auto mt-10 md:mt-14 text-center">
            <p className="text-sm text-tie-text-muted leading-relaxed">
              Each Innovation Cell operates as a peer-to-peer learning network with
              expert coordination, evidence-led case exchange, and
              action-to-implementation follow-through. Cells converge at the
              Innovation Help Desk for technical guidance and knowledge repository access.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}